/**
 * XoeActionCard — renders a single proposed assistant action.
 *
 * Three rendering modes:
 *   - confirmed  : compact success strip (no form, no buttons)
 *   - write       : editable ScratchpadDraftForm + Confirm / Dismiss
 *   - open        : title + Open button (client-side only, no confirm endpoint) + Dismiss
 *
 * Write actions call useConfirmXoeAction on confirm; on success the conversation query
 * is invalidated so the thread refetches and action.status flips to 'confirmed'.
 * Open actions never touch the confirm endpoint.
 * Dismiss delegates to the parent's `onDismiss`, which POSTs to healthcare's dismiss
 * endpoint so the rejection is recorded in the audit trail, and hides the card only once
 * that succeeds. See XoeActionCardList — this component's prop contract is unchanged.
 */

import React, { useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Divider, List, Row, Space, Typography } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, LinkOutlined, ThunderboltOutlined, UndoOutlined, WarningOutlined } from '@ant-design/icons';
import axios from 'axios';

import ScratchpadDraftForm from '../../../pages/healthcare/patient/modalComponents/submodals/ScratchpadDraftForm';
import InlineLoading from '../../shared/InlineLoading';
import { useNotifications } from '../../../context/NotificationContext';
import {
	useConfirmXoeAction,
	useRecordXoeOpenAction,
	useResolveXoePatient,
	useUpdateXoeConversation,
} from '../../../hooks/xoe/useXoe';
import { useWheelSnpEvidence } from '../../../hooks/genetic/useWheelActions';
import { useReferenceModal } from '../../../hooks/xoe-actions/useReferenceModal';
import { useOpenPatientModal } from '../../../hooks/xoe-actions/useOpenPatientModal';
import { extractValidationErrors } from '../../../utils/apiError';
import { isWheelActionType, wheelSnpEntries, WheelSnpEntry } from '../../../utils/geneCodes/wheelActions';
import { formatPValue, subPhenotypeFrom } from '../../../utils/geneCodes/pValue';
import type { WheelActionItemResult, WheelUndoItemResult } from '../../../types/geneCodes/WheelActionTypes';
import type { CatalogueVariant } from '../../../types/geneCodes/VariantCatalogueTypes';
import type { XoePatientCandidate, XoeProposedAction } from '../../../types/xoe/XoeTypes';
import type { EvidenceRef, EvidenceSource } from '../../../types/healthcare/PatientAnalysisTypes';
import type { XoeActionCardProps } from '../../../types/xoe/XoeUiTypes';

const { Text } = Typography;

// ── Action-type → scratchpad section map ──────────────────────────────────────

const WRITE_SECTION_MAP: Record<string, string> = {
	create_medical_history:   'medical_history',
	create_medication:        'medications',
	create_vital_signs:       'vital_signs',
	create_referral:          'referrals',
	create_test_order:        'test_order',
	create_supplement_regimen:'supplement_regimen',
	create_patient_note:      'patient_note',
};

// Map open_record.params.type → EvidenceRef.source so useReferenceModal can open the right modal
const RECORD_TYPE_TO_SOURCE: Record<string, EvidenceSource> = {
	medication:      'medications',
	medical_history: 'medical_history',
	test_order:      'lab_results',
	blood_test:      'blood_results',
	lab_results:     'lab_results',
	blood_results:   'blood_results',
};

// ── Wheel actions (C5) — confirm goes to LABIQ, never healthcare ──────────────

const WHEEL_UNDO_REASONS: Record<string, string> = {
	wheel_not_empty: 'wheel not empty',
	trait_holds_snps: 'trait holds SNPs',
	already_gone: 'already removed',
	pre_existing: 'existed before this change',
};

const countOf = (value: number | undefined, noun: string): string => `${value ?? 0} ${noun}${(value ?? 0) === 1 ? '' : 's'}`;

const wheelConfirmedSummary = (action: XoeProposedAction, item: WheelActionItemResult | null): string => {
	switch (action.action_type) {
		case 'create_wheel':
			return 'Created wheel draft';
		case 'create_wheel_trait':
			return 'Created trait';
		case 'add_wheel_snps':
			return `Added ${countOf(item?.created_count, 'SNP')}`;
		case 'remove_wheel_snp':
			return `Removed ${countOf(item?.deleted_count, 'SNP')}`;
		case 'add_wheel_gene':
			return item?.already_present ? 'Gene already on the ring — no change' : 'Added gene to the ring';
		default:
			return action.title;
	}
};

const wheelUndoneSummary = (entry: WheelUndoItemResult | null): string => {
	if (!entry) return 'Undone';
	if (entry.deleted === false && entry.reason) return `Kept — ${WHEEL_UNDO_REASONS[entry.reason] ?? entry.reason}`;
	if (entry.action === 'add_wheel_snps') return `Removed ${countOf(entry.deleted_count, 'SNP')}`;
	if (entry.action === 'remove_wheel_snp') {
		const restored = `Restored ${countOf(entry.restored_staging_ids?.length, 'SNP')}`;
		return entry.skipped_count ? `${restored} · ${entry.skipped_count} re-added by hand — skipped` : restored;
	}
	return 'Undone';
};

const wheelParamText = (params: Record<string, unknown>, key: string): string | null => {
	const value = params[key];
	return typeof value === 'string' && value.trim() !== '' ? value : null;
};

// ── Component ─────────────────────────────────────────────────────────────────

const XoeActionCard: React.FC<XoeActionCardProps> = ({
	action,
	messageId,
	conversationUuid,
	patientId,
	precedingUserText,
	onAutoContinue,
	onDismiss,
	wheel,
}) => {
	// Draft state seeded from action.params (editable copy)
	const [editedDraft, setEditedDraft] = useState<Record<string, any>>({
		...action.params,
	});
	const [confirmError, setConfirmError] = useState<string | null>(null);
	// True only when a create_medication confirm 422s on the MedicationSafetyScreener — gates
	// the deliberate "override and save" control. Never set for other 422s.
	const [safetyBlocked, setSafetyBlocked] = useState(false);

	const { showError, showWarning, showInfo } = useNotifications();
	const confirmMutation = useConfirmXoeAction();
	const recordOpenMutation = useRecordXoeOpenAction();

	// useReferenceModal must always be called (Rules of Hooks); patientId ?? "" degrades gracefully
	const { openRef, modals } = useReferenceModal(patientId ?? "");

	// Open-by-name (S6): resolve a patient name → bind → open the patient modal in place.
	// All hooks are called unconditionally at the top (Rules of Hooks).
	const { openPatient, modals: patientModals } = useOpenPatientModal();
	const resolveMutation = useResolveXoePatient();
	const bindMutation = useUpdateXoeConversation();
	const [candidates, setCandidates] = useState<XoePatientCandidate[] | null>(null);
	const [resolving, setResolving] = useState(false);
	// Guards the resolve-driven auto-continue to fire at most once per card instance (S8).
	const autoContinuedRef = useRef(false);

	// Wheel evidence — hooks stay unconditional (Rules of Hooks); disabled off the builder pane.
	const snpEntries = useMemo(() => (action.action_type === 'add_wheel_snps' ? wheelSnpEntries(action.params) : []), [action]);
	const evidenceQuery = useWheelSnpEvidence(
		snpEntries.map((entry) => entry.rs),
		wheel?.canConfirm ? wheel.panelId : null,
	);

	// ── Wheel actions (C5) ─────────────────────────────────────────────────────

	if (isWheelActionType(action.action_type)) {
		const phase = wheel?.phase ?? 'idle';
		const pending = phase === 'pending';

		if (wheel && phase === 'confirmed') {
			const skipped = wheel.itemResult?.skipped_count ?? 0;
			return (
				<Card
					size="small"
					data-testid="xoe-wheel-confirmed"
					style={{ marginTop: 'var(--mg-sp-3)', background: 'var(--mg-success-bg)', border: '1px solid var(--mg-success)' }}
				>
					<Space direction="vertical" size={2} style={{ width: '100%' }}>
						<Space>
							<CheckCircleOutlined style={{ color: 'var(--mg-success)' }} />
							<Text type="secondary" style={{ fontSize: 'var(--mg-fs-md)' }}>
								{`${wheel.alreadyConfirmed ? 'Already confirmed · ' : ''}${wheelConfirmedSummary(action, wheel.itemResult)}`}
							</Text>
							<Button
								size="small"
								type="text"
								icon={<UndoOutlined />}
								onClick={wheel.onUndo}
								loading={wheel.undoPending}
								disabled={wheel.undoPending}
								data-testid="xoe-wheel-undo"
							>
								Undo
							</Button>
						</Space>
						{skipped > 0 && (
							<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)' }} data-testid="xoe-wheel-skipped">
								{`${skipped} already in this wheel — skipped, no duplicate created`}
							</Text>
						)}
						{wheel.forkedFromPanelId != null && (
							<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)' }} data-testid="xoe-wheel-fork-note">
								Published wheel forked — applied to the new draft
							</Text>
						)}
						{wheel.undoBlocked && <Alert message={wheel.undoBlocked} type="warning" showIcon data-testid="xoe-wheel-undo-blocked" />}
						{wheel.undoError && <Alert message={wheel.undoError} type="error" showIcon data-testid="xoe-wheel-undo-error" />}
					</Space>
				</Card>
			);
		}

		if (wheel && phase === 'undone') {
			return (
				<Card size="small" data-testid="xoe-wheel-undone" style={{ marginTop: 'var(--mg-sp-3)', border: '1px solid var(--mg-border)' }}>
					<Space>
						<UndoOutlined style={{ color: 'var(--mg-text-secondary)' }} />
						<Text type="secondary" style={{ fontSize: 'var(--mg-fs-md)' }}>
							{`${wheel.alreadyUndone ? 'Already undone · ' : ''}${wheelUndoneSummary(wheel.undoEntry)}`}
						</Text>
					</Space>
				</Card>
			);
		}

		const canConfirm = wheel?.canConfirm === true;
		const evidence = evidenceQuery.data;
		const failing = wheel?.failingItem ?? null;

		return (
			<Card
				size="small"
				data-testid="xoe-wheel-card"
				title={<Text style={{ fontSize: 'var(--mg-fs-md)' }}>{action.title}</Text>}
				extra={
					<Space size={4}>
						{canConfirm && (
							<Button size="small" type="primary" onClick={wheel?.onConfirm} loading={pending} disabled={pending} data-testid="xoe-wheel-confirm">
								Confirm
							</Button>
						)}
						<Button size="small" type="text" onClick={() => onDismiss(action.id)} disabled={pending}>
							Dismiss
						</Button>
					</Space>
				}
				style={{ marginTop: 'var(--mg-sp-3)' }}
			>
				<Space direction="vertical" size={4} style={{ width: '100%' }}>
					{!canConfirm && (
						<Alert message="Open this wheel in the builder to apply the proposal." type="info" showIcon data-testid="xoe-wheel-no-target" />
					)}

					{action.action_type === 'create_wheel' && (
						<Text style={{ fontSize: 'var(--mg-fs-md)' }}>{`New wheel: ${wheelParamText(action.params, 'name_panel') ?? '—'}`}</Text>
					)}
					{action.action_type === 'create_wheel_trait' && (
						<Text style={{ fontSize: 'var(--mg-fs-md)' }}>{`New trait: ${wheelParamText(action.params, 'trait_name') ?? '—'}`}</Text>
					)}
					{action.action_type === 'remove_wheel_snp' && (
						<Text style={{ fontSize: 'var(--mg-fs-md)' }}>
							{`Remove ${wheelParamText(action.params, 'rs') ?? '—'} from ${wheelParamText(action.params, 'trait_name') ?? 'unassigned'}`}
						</Text>
					)}
					{action.action_type === 'add_wheel_gene' && (
						<Text style={{ fontSize: 'var(--mg-fs-md)' }}>
							{`Add ${wheelParamText(action.params, 'gene_symbol') ?? '—'} to the gene ring of ${wheelParamText(action.params, 'trait_name') ?? '—'}`}
						</Text>
					)}

					{snpEntries.map((entry: WheelSnpEntry) => {
						const variant: CatalogueVariant | null = evidence?.[entry.rs] ?? null;
						const sub = variant ? subPhenotypeFrom(variant.p_value_text) : null;
						const onArray = variant ? (variant.on_array === true ? 'on' : variant.on_array === false ? 'off' : 'unknown') : null;
						return (
							<div key={`${entry.rs}:${entry.subcategory ?? ''}`} data-testid="xoe-wheel-snp-row" data-rs={entry.rs}>
								<Text className="mg-mono-num" style={{ fontSize: 'var(--mg-fs-md)' }}>
									{entry.rs}
								</Text>
								<Text style={{ fontSize: 'var(--mg-fs-md)' }}>
									{` · ${entry.gene ?? variant?.gene ?? '—'} · risk ${entry.risk_allele ?? '—'} · ${
										typeof entry.subcategory === 'string' && entry.subcategory.trim() !== '' ? entry.subcategory : 'unassigned'
									}`}
								</Text>
								{variant ? (
									<>
										<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)', display: 'block' }}>
											<span data-testid="xoe-wheel-p">{`p ${formatPValue(variant.p_value) ?? '—'}`}</span>
											{/* No population: the catalogue hard-codes it null, so it stated nothing on every row. */}
											{` · ${variant.pmids.length} PMID${variant.pmids.length === 1 ? '' : 's'}`}
										</Text>
										{sub && (
											<Text type="secondary" italic style={{ fontSize: 'var(--mg-fs-xs)', display: 'block' }} data-testid="xoe-wheel-subphenotype">
												{sub}
											</Text>
										)}
										<Text
											style={{ fontSize: 'var(--mg-fs-xs)', display: 'block', color: onArray === 'off' ? 'var(--mg-danger)' : 'var(--mg-text-secondary)' }}
											data-testid="xoe-wheel-on-array"
											data-state={onArray ?? 'unknown'}
										>
											{onArray === 'off' ? (
												<>
													<WarningOutlined /> Not measurable on the array
												</>
											) : onArray === 'on' ? (
												'Measurable on the array'
											) : (
												'Measurability unknown'
											)}
										</Text>
										{variant.membership.in_current_panel && (
											<Text style={{ fontSize: 'var(--mg-fs-xs)', display: 'block', color: 'var(--mg-warning)' }} data-testid="xoe-wheel-already">
												<ExclamationCircleOutlined /> Already in this wheel — confirming skips it
											</Text>
										)}
									</>
								) : (
									canConfirm && (
										<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)', display: 'block' }} data-testid="xoe-wheel-no-evidence">
											{evidenceQuery.isLoading ? 'Checking catalogue…' : 'No catalogue evidence found'}
										</Text>
									)
								)}
							</div>
						);
					})}

					{phase === 'failed' && (
						<Alert
							type="error"
							showIcon
							data-testid="xoe-wheel-error"
							message={failing && !failing.mine ? 'Nothing applied — the batch rolled back' : wheel?.error ?? 'Could not apply this change. Please try again.'}
							description={
								failing
									? failing.mine
										? `${failing.action_type}: ${Object.values(failing.errors ?? {}).flat().join(' ')}`
										: `Item ${failing.index + 1} (${failing.action_type}) failed — nothing was applied.`
									: undefined
							}
						/>
					)}
				</Space>
			</Card>
		);
	}

	// ── Confirmed ──────────────────────────────────────────────────────────────

	if (action.status === 'confirmed') {
		return (
			<Card
				size="small"
				style={{
					marginTop: 'var(--mg-sp-3)',
					background: 'var(--mg-success-bg)',
					border: '1px solid var(--mg-success)',
				}}
			>
				<Space>
					<CheckCircleOutlined style={{ color: 'var(--mg-success)' }} />
					<Text type="secondary" style={{ fontSize: 'var(--mg-fs-md)' }}>
						{action.linked_item_type
							? `Created ${action.linked_item_type}${action.linked_item_id ? ` #${action.linked_item_id}` : ''}`
							: action.title}
					</Text>
				</Space>
			</Card>
		);
	}

	// ── Open actions ───────────────────────────────────────────────────────────

	if (action.kind === 'open') {
		// Bind the chosen candidate, best-effort audit, then open the patient modal in
		// place (no navigation). Shared by the auto-bind (1 candidate) and picker (N) paths.
		const bindAndOpen = async (candidate: XoePatientCandidate) => {
			try {
				await bindMutation.mutateAsync({
					uuid: conversationUuid,
					patient_id: candidate.patient_id,
					title: candidate.display_name,
				});
				// Best-effort audit — never blocks the open.
				recordOpenMutation.mutate({ messageId, action_id: action.id });
				openPatient(candidate.patient_id);
				setCandidates(null);
				// S8: after a resolve-driven bind, re-send the ORIGINAL triggering user
				// message so the now patient-grounded turn re-proposes the write/open in one
				// step. Fires at most once per card instance (the ref guard); skipped when
				// there is no triggering text to replay. Routed through the chat window's
				// send (onAutoContinue → the session's send) so the "thinking" indicator
				// shows and the composer locks while the summary generates — the practitioner
				// gets a clear signal and cannot fire a competing message.
				if (
					typeof precedingUserText === 'string' &&
					precedingUserText.length > 0 &&
					!autoContinuedRef.current
				) {
					autoContinuedRef.current = true;
					onAutoContinue(precedingUserText);
				}
			} catch {
				showError('Could not link the patient. Please try again.');
			}
		};

		const handleOpen = async () => {
			if (action.action_type === 'open_patient_record') {
				// Already bound → keep the Review-4 target: the CURRENT conversation patient,
				// never the model-supplied params.patient_id. Opens the patient modal in
				// place (no navigation), the same as the resolve-driven path above. Audit
				// only once the guard passes (defect 4).
				if (patientId) {
					recordOpenMutation.mutate({ messageId, action_id: action.id });
					openPatient(patientId);
					return;
				}

				// Unbound but the model named a patient → resolve by name (S6).
				const query =
					typeof action.params.query === 'string' ? action.params.query.trim() : '';
				if (query) {
					setResolving(true);
					try {
						const list = await resolveMutation.mutateAsync({
							uuid: conversationUuid,
							query,
						});
						if (!list || list.length === 0) {
							showWarning(`No patient found matching "${query}".`);
						} else if (list.length === 1) {
							await bindAndOpen(list[0]);
						} else {
							// N candidates → show the in-chat picker; bind only on select.
							setCandidates(list);
						}
					} catch {
						showError('Could not search for a patient. Please try again.');
					} finally {
						setResolving(false);
					}
					return;
				}

				// No bound patient and no name to search.
				showWarning('This conversation is not linked to a patient.');
				return;
			}

			// open_record: { type, id } → useReferenceModal
			if (!patientId) {
				showWarning('Cannot open record — this conversation is not patient-scoped.');
				return;
			}
			const params = action.params as { type?: string; id?: number };
			const source = params.type ? RECORD_TYPE_TO_SOURCE[params.type] : undefined;
			if (!source) {
				showInfo('Record type not supported for direct navigation.');
				return;
			}
			// Audit only after all guards pass (defect 4) — best-effort, never blocks the open.
			recordOpenMutation.mutate({ messageId, action_id: action.id });
			const ref: EvidenceRef = {
				source,
				record_id: params.id !== undefined ? String(params.id) : undefined,
			};
			openRef(ref);
		};

		return (
			<>
				<div style={{ marginTop: 'var(--mg-sp-3)' }}>
					{candidates ? (
						<>
							<Text style={{ fontSize: 'var(--mg-fs-md)', display: 'block', marginBottom: 'var(--mg-sp-3)' }}>
								Select the patient to open:
							</Text>
							<List
								size="small"
								dataSource={candidates}
								rowKey={(c) => c.patient_id}
								renderItem={(c) => (
									<List.Item
										actions={[
											<Button
												key="select"
												size="small"
												type="primary"
												loading={bindMutation.isPending}
												onClick={() => void bindAndOpen(c)}
											>
												Select
											</Button>,
										]}
									>
										<Space direction="vertical" size={0}>
											<Text style={{ fontSize: 'var(--mg-fs-md)' }}>{c.display_name}</Text>
											<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)' }}>
												{`DOB: ${c.dob ?? '—'}`}
											</Text>
										</Space>
									</List.Item>
								)}
							/>
							<div style={{ textAlign: 'right', marginTop: 'var(--mg-sp-2)' }}>
								<Button size="small" type="text" onClick={() => setCandidates(null)}>
									Cancel
								</Button>
							</div>
						</>
					) : (
						<Space direction="vertical" size="small">
							<Space direction="horizontal" size="small">
								<Button
									size="small"
									type="primary"
									loading={resolving}
									onClick={() => void handleOpen()}
								>
									Open
								</Button>
								<Button size="small" type="text" onClick={() => onDismiss(action.id)}>
									Dismiss
								</Button>
							</Space>
						</Space>
					)}
				</div>
				{modals}
				{patientModals}
			</>
		);
	}

	// ── Server actions ─────────────────────────────────────────────────────────
	// Server actions trigger a backend job on confirm — no draft form, no params needed.

	if (action.kind === 'server') {
		const patientLessServer = patientId === null;

		const handleServerConfirm = () => {
			setConfirmError(null);
			confirmMutation.mutate(
				{ messageId, actionId: action.id, draft: {}, conversationUuid },
				{
					onError: (error) => {
						const serverMsgs = extractValidationErrors(error);
						const hasMsg =
							axios.isAxiosError(error) &&
							!!(error.response?.data?.message || error.response?.data?.errors);
						setConfirmError(
							hasMsg && serverMsgs.length > 0
								? serverMsgs.join(' ')
								: 'Failed to trigger server action. Please try again.',
						);
					},
				},
			);
		};

		return (
			<Card
				size="small"
				title={
					<Space>
						<ThunderboltOutlined style={{ color: 'var(--mg-primary)' }} />
						<Text style={{ fontSize: 'var(--mg-fs-md)' }}>{action.title}</Text>
					</Space>
				}
				extra={
					<Space size={4}>
						{!patientLessServer && (
							<Button
								size="small"
								type="primary"
								onClick={handleServerConfirm}
								loading={confirmMutation.isPending}
								disabled={confirmMutation.isPending}
							>
								Confirm
							</Button>
						)}
						<Button
							size="small"
							type="text"
							onClick={() => onDismiss(action.id)}
							disabled={confirmMutation.isPending}
						>
							Dismiss
						</Button>
					</Space>
				}
				style={{ marginTop: 'var(--mg-sp-3)' }}
			>
				{patientLessServer ? (
					<Alert
						message="This conversation isn't linked to a patient, so this action can't be performed."
						type="info"
						showIcon
					/>
				) : (
					<Text type="secondary" style={{ fontSize: 'var(--mg-fs-md)' }}>
						{action.action_type === 'generate_comprehensive_analysis'
							? 'Queues a comprehensive analysis job for this patient.'
							: 'This action will be performed on the server.'}
					</Text>
				)}
				{confirmError && (
					<Alert
						message={confirmError}
						type="error"
						showIcon
						style={{ marginTop: 'var(--mg-sp-4)' }}
						closable
						onClose={() => setConfirmError(null)}
					/>
				)}
			</Card>
		);
	}

	// ── Write actions ──────────────────────────────────────────────────────────

	const section = WRITE_SECTION_MAP[action.action_type];
	if (!section) {
		// Unknown action_type — render nothing rather than crashing
		return null;
	}

	// Global (patient-less) conversations cannot persist write actions — the confirm
	// endpoint 422s without patient context. Surface that up-front instead of offering a
	// Confirm button that is guaranteed to fail (defect 3).
	const patientLess = patientId === null;

	// Surface the REAL server cause (e.g. the 422 message) rather than a misleading generic
	// toast (defect 3). Fall back to the generic string only when the error carries no
	// server-provided message.
	const surfaceConfirmError = (error: unknown): string => {
		const serverMessages = extractValidationErrors(error);
		const hasServerMessage =
			axios.isAxiosError(error) &&
			!!(error.response?.data?.message || error.response?.data?.errors);
		return hasServerMessage && serverMessages.length > 0
			? serverMessages.join(' ')
			: 'Failed to create record. Please try again.';
	};

	// Detect the medication safety-screen 422 specifically — must NOT trigger on other 422s
	// (e.g. a missing medication_name) so the override only surfaces when it would change the
	// outcome. Matches the MedicationSafetyScreener::throw shapes (duplicate / interaction).
	const isMedicationSafetyBlock = (serverMessage: string): boolean => {
		if (action.action_type !== 'create_medication') {
			return false;
		}
		const lower = serverMessage.toLowerCase();
		return (
			lower.includes('duplicate medication') ||
			lower.includes('bleeding-risk interaction')
		);
	};

	const handleConfirm = () => {
		setConfirmError(null);
		setSafetyBlocked(false);
		confirmMutation.mutate(
			{ messageId, actionId: action.id, draft: editedDraft, conversationUuid },
			{
				onError: (error) => {
					const serverMessage = surfaceConfirmError(error);
					setConfirmError(serverMessage);
					setSafetyBlocked(isMedicationSafetyBlock(serverMessage));
				},
				onSuccess: () => {
					setSafetyBlocked(false);
				},
			},
		);
	};

	// Deliberate override: re-submit the SAME draft with safetyOverride so MedicationCreator
	// skips the safety screen. Only ever fired by the explicit acknowledgement click.
	const handleOverrideConfirm = () => {
		setConfirmError(null);
		confirmMutation.mutate(
			{ messageId, actionId: action.id, draft: editedDraft, conversationUuid, safetyOverride: true },
			{
				onError: (error) => {
					const serverMessage = surfaceConfirmError(error);
					setConfirmError(serverMessage);
					// Keep the override visible if it blocks on the safety screen again; for any
					// other failure hide it and just show the message.
					setSafetyBlocked(isMedicationSafetyBlock(serverMessage));
				},
				onSuccess: () => {
					setSafetyBlocked(false);
				},
			},
		);
	};

	return (
		<Card
			size="small"
			title={
				<Space>
					<Text style={{ fontSize: 'var(--mg-fs-md)' }}>{action.title}</Text>
				</Space>
			}
			extra={
				<Space size={4}>
					{!patientLess && (
						<Button
							size="small"
							type="primary"
							onClick={handleConfirm}
							loading={confirmMutation.isPending}
							disabled={confirmMutation.isPending}
						>
							Confirm
						</Button>
					)}
					<Button
						size="small"
						type="text"
						onClick={() => onDismiss(action.id)}
						disabled={confirmMutation.isPending}
					>
						Dismiss
					</Button>
				</Space>
			}
			style={{ marginTop: 'var(--mg-sp-3)' }}
		>
			{patientLess ? (
				<Alert
					message="This conversation isn't linked to a patient, so write actions can't be saved here."
					type="info"
					showIcon
				/>
			) : (
				<>
					{confirmMutation.isPending ? (
						<InlineLoading minHeight={48} message="Saving…" />
					) : (
						<ScratchpadDraftForm
							section={section}
							value={editedDraft}
							onChange={setEditedDraft}
						/>
					)}

					{confirmError && (
						<Alert
							message={confirmError}
							type="error"
							showIcon
							style={{ marginTop: 'var(--mg-sp-4)' }}
							closable
							onClose={() => setConfirmError(null)}
						/>
					)}

					{safetyBlocked && (
						<div style={{ marginTop: 'var(--mg-sp-4)' }}>
							<Button
								size="small"
								danger
								onClick={handleOverrideConfirm}
								loading={confirmMutation.isPending}
								disabled={confirmMutation.isPending}
							>
								I acknowledge this interaction — override and save
							</Button>
							<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)', display: 'block', marginTop: 'var(--mg-sp-2)' }}>
								This bypasses the medication safety check and saves the record anyway.
							</Text>
						</div>
					)}

					<Divider style={{ margin: 'var(--mg-sp-4) 0 0' }} />
					<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)' }}>
						Edit the draft above, then click Confirm to create the record.
					</Text>
				</>
			)}
		</Card>
	);
};

export default XoeActionCard;
